// Dashboard URLs carry an optional next-i18next locale prefix, so /o/ is not always the first path segment.
export const DASHBOARD_PAGE_URL_PATTERN = /^https?:\/\/[^/]+(\/[a-z]{2}(-[A-Z]{2})?)?\/o\//;

const DASHBOARD_PATHNAME_PATTERN = /^(\/[a-z]{2}(-[A-Z]{2})?)?\/o\//;

const LOCALE_PATH_PREFIX = /^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/)/;

const AMPLITUDE_ELEMENT_CLICKED_EVENT = "[Amplitude] Element Clicked";
const AMPLITUDE_ELEMENT_IDENTITY_PROPERTIES = [
  "[Amplitude] Element Text",
  "[Amplitude] Element Aria Label",
  "[Amplitude] Element ID",
];
const AMPLITUDE_PAGE_PATH_PROPERTY = "[Amplitude] Page Path";
const AMPLITUDE_PAGE_URL_PROPERTY = "[Amplitude] Page URL";

export const isDashboardPathname = (pathname: string | null): boolean =>
  Boolean(pathname && DASHBOARD_PATHNAME_PATTERN.test(pathname));

const stripLocaleFromPath = (path: string): string => path.replace(LOCALE_PATH_PREFIX, "");

type DashboardClickEvent = {
  event_type: string;
  event_properties?: object;
};

const hasElementIdentity = (properties: Record<string, unknown>): boolean =>
  AMPLITUDE_ELEMENT_IDENTITY_PROPERTIES.some((name) => {
    const value = properties[name];
    return typeof value === "string" && value.trim().length > 0;
  });

const pathnameFromPageUrl = (pageUrl: string): string | undefined => {
  try {
    return new URL(pageUrl).pathname;
  } catch {
    return undefined;
  }
};

export const prepareDashboardElementClick = <Event extends DashboardClickEvent>(
  event: Event,
  sessionId: string
): Event | null => {
  if (event.event_type !== AMPLITUDE_ELEMENT_CLICKED_EVENT) {
    return event;
  }

  const properties: Record<string, unknown> = {};
  if (event.event_properties) {
    Object.assign(properties, event.event_properties);
  }
  const pageUrl = properties[AMPLITUDE_PAGE_URL_PROPERTY];
  if (typeof pageUrl !== "string" || !DASHBOARD_PAGE_URL_PATTERN.test(pageUrl)) {
    return event;
  }

  if (!hasElementIdentity(properties)) {
    return null;
  }

  const pagePath =
    typeof properties[AMPLITUDE_PAGE_PATH_PROPERTY] === "string"
      ? properties[AMPLITUDE_PAGE_PATH_PROPERTY]
      : pathnameFromPageUrl(pageUrl);

  return {
    ...event,
    event_properties: {
      ...properties,
      ...(typeof pagePath === "string" ? { [AMPLITUDE_PAGE_PATH_PROPERTY]: stripLocaleFromPath(pagePath) } : {}),
      zeffySessionId: sessionId,
    },
  } as Event;
};
